import {
  ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges,
} from "@angular/core";
import { CheckResult, ReadinessCheck, ReadinessInstance, ReadinessResult, ReadinessState } from "../../models/schedule.model";

interface Row {
  key: string;
  name: string;
  id: string; // instance id for server rows, "" for checks
  detail: string;
  fix: string;
  result: CheckResult;
}

interface Section {
  key: string;
  title: string;
  sub: string;
  badge: string;
  badgeClass: string;
  open: boolean; // starts expanded when something needs attention
  flat: boolean; // schedule section: one sorted list, no grouping
  rows: Row[]; // flat sections only
  shown: Row[]; // top failures
  hidden: Row[]; // remaining failures, behind "N more failed"
  warns: Row[];
  passedRows: Row[];
  passedText: string;
}

const ORDER: Record<string, number> = { fail: 0, warn: 1, pass: 2 };
const SHOWN_FAILURES = 3;

/** Dialog that shows the result of action "check_schedule". Presentational only. */
@Component({
  selector: "app-check-schedule",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ["./check-schedule.component.css"],
  templateUrl: "./check-schedule.component.html",
})
export class CheckScheduleComponent implements OnChanges {
  @Input() result: ReadinessResult | null = null;
  @Input() state: ReadinessState = "idle";
  @Input() error = "";

  @Output() recheck = new EventEmitter<void>();
  @Output() dismiss = new EventEmitter<void>();

  sections: Section[] = [];
  private expanded = new Set<string>();

  ngOnChanges(ch: SimpleChanges): void {
    if (ch["result"]) {
      this.expanded.clear();
      this.sections = this.result ? this.build(this.result) : [];
    }
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    this.dismiss.emit();
  }

  // ---- Banner ----------------------------------------------------------

  get bannerClass(): string {
    const r = this.result;
    return !r || !r.ready ? "bad" : r.summary.warnings > 0 ? "warn" : "ok";
  }

  get bannerIcon(): string {
    const c = this.bannerClass;
    return c == "ok" ? "fa-check-circle" : c == "warn" ? "fa-exclamation-triangle" : "fa-times-circle";
  }

  get bannerTitle(): string {
    const c = this.bannerClass;
    return c == "ok" ? "Ready to patch" : c == "warn" ? "Ready, with warnings" : "Not ready";
  }

  get summaryText(): string {
    const s = this.result?.summary;
    if (!s) {
      return "";
    }
    return `${s.servers_ready} of ${s.servers} servers ready \u00b7 ${s.failed} failed \u00b7 ${s.warnings} warning${
      s.warnings == 1 ? "" : "s"
    }`;
  }

  /** checked_at is UTC; show it in IST, e.g. "Checked today at 13:46 IST". */
  get checkedText(): string {
    const iso = this.result?.checked_at;
    const d = iso ? new Date(iso) : null;
    if (!d || isNaN(d.getTime())) {
      return "";
    }
    const tz = "Asia/Kolkata";
    const day = (x: Date) => x.toLocaleDateString("en-CA", { timeZone: tz });
    const time = d.toLocaleTimeString("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false });
    const when = day(d) == day(new Date())
      ? "today"
      : d.toLocaleDateString("en-GB", { timeZone: tz, day: "numeric", month: "short" });
    return `Checked ${when} at ${time} IST`;
  }

  // ---- Template helpers ------------------------------------------------

  icon(r: string): string {
    return r == "pass" ? "fa-check-circle" : r == "warn" ? "fa-exclamation-triangle" : "fa-times-circle";
  }

  hiddenText(s: Section): string {
    const names = s.hidden.map((h) => (h.id ? h.name : h.name.toLowerCase())).join(", ");
    return `${s.hidden.length} more failed: ${names}`;
  }

  toggle(key: string): void {
    this.expanded.has(key) ? this.expanded.delete(key) : this.expanded.add(key);
  }

  isOpen(key: string): boolean {
    return this.expanded.has(key);
  }

  trackKey(_: number, x: { key: string }) {
    return x.key;
  }

  // ---- Building the view -----------------------------------------------

  private build(r: ReadinessResult): Section[] {
    const out: Section[] = [];

    out.push(this.makeSection("schedule", "Schedule", "", this.rows("sch", r.schedule_checks), [], true));

    (r.accounts || []).forEach((a, i) => {
      out.push(
        this.makeSection(
          `${a.account_id}-${a.region}-${i}`,
          a.account_name,
          a.region,
          this.rows(`${a.account_id}-c`, a.checks),
          this.rows(`${a.account_id}-s`, a.instances),
          false
        )
      );
    });
    return out;
  }

  private rows(prefix: string, list?: (ReadinessCheck | ReadinessInstance)[] | null): Row[] {
    return (list || []).map((c, i) => {
      const inst = c as ReadinessInstance;
      return {
        key: `${prefix}-${i}`,
        name: inst.instance_id ? inst.name || inst.instance_id : c.name,
        id: inst.instance_id ? (inst.name ? inst.instance_id : "") : "",
        detail: c.detail,
        fix: c.fix,
        result: c.result,
      };
    });
  }

  private makeSection(
    key: string, title: string, sub: string, checks: Row[], servers: Row[], flat: boolean
  ): Section {
    const failChecks = checks.filter((x) => x.result == "fail");
    const failServers = servers.filter((x) => x.result == "fail");
    const warns = [...checks, ...servers].filter((x) => x.result == "warn");
    const passChecks = checks.filter((x) => x.result == "pass");
    const passServers = servers.filter((x) => x.result == "pass");
    const failed = failChecks.length + failServers.length;

    // Failing servers are the specific things someone has to act on, so they come first.
    // Without any, show the top failing checks. Everything else sits behind "N more failed".
    let shown: Row[];
    let hidden: Row[];
    if (failServers.length) {
      shown = failServers.slice(0, SHOWN_FAILURES);
      hidden = [...failServers.slice(SHOWN_FAILURES), ...failChecks];
    } else {
      shown = failChecks.slice(0, SHOWN_FAILURES);
      hidden = failChecks.slice(SHOWN_FAILURES);
    }

    const passed = passChecks.length + passServers.length;
    const parts: string[] = [];
    if (passChecks.length) {
      parts.push(`${passChecks.length} check${passChecks.length == 1 ? "" : "s"}`);
    }
    if (passServers.length) {
      parts.push(`${passServers.length} server${passServers.length == 1 ? "" : "s"}`);
    }

    let badge = `${passed} passed`;
    let badgeClass = "b-ok";
    if (failed > 0) {
      badge = `${failed} failed`;
      badgeClass = "b-fail";
    } else if (warns.length > 0) {
      badge = `${warns.length} warning${warns.length == 1 ? "" : "s"}`;
      badgeClass = "b-warn";
    }

    return {
      key,
      title,
      sub,
      badge,
      badgeClass,
      open: failed > 0 || warns.length > 0,
      flat,
      rows: flat ? [...checks, ...servers].sort((a, b) => ORDER[a.result] - ORDER[b.result]) : [],
      shown,
      hidden,
      warns,
      passedRows: [...passChecks, ...passServers],
      passedText: parts.length ? `${parts.join(" and ")} passed` : "",
    };
  }
}